/**
 * File and Media Constants
 *
 * Contains constants related to file handling, media types, and extensions
 */

/**
 * Supported image file extensions (with dot prefix)
 * Single source of truth for image file types
 */
export const IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.svg',
  '.bmp',
] as const;

/**
 * Accepted file types for audit question file uploads
 * Includes images, documents, spreadsheets, and videos
 */
export const AUDIT_UPLOAD_ACCEPT_TYPES =
  '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp4,.mov,.avi,.wmv,.flv,.mkv,.webm';

/**
 * Helper function to normalize extension (add dot if missing)
 * @param extension - File extension (with or without dot)
 * @returns Extension with dot prefix
 */
const normalizeExtension = (extension: string): string => {
  const lower = extension.toLowerCase();
  return lower.startsWith('.') ? lower : `.${lower}`;
};

/**
 * Helper function to check if a file extension is an image
 * @param extension - File extension (with or without dot)
 * @returns true if extension is an image type
 */
export const isImageExtension = (extension: string): boolean => {
  const normalized = normalizeExtension(extension);
  return IMAGE_EXTENSIONS.includes(normalized as any);
};

/**
 * Legacy export for backward compatibility
 * @deprecated Use isImageExtension() instead which handles both formats
 */
export const IMAGE_EXTENSIONS_NO_DOT = IMAGE_EXTENSIONS.map((ext) => ext.slice(1));
